import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
  ProcessEdgeForValidation,
  ProcessFlowchartErrorType,
  ProcessNodeForValidation,
  ValidationContext,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  getFlowchartNodeFromClientId,
  getTaskNodesUpstreamOfNode,
} from 'processes/graph-builder-processes/validation/ProcessGraphGetters';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  FlowchartChangeGenerator,
  FlowchartChanges,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

import {FlowchartValidator} from '../../platform/FlowchartValidator';
import {pushErrors} from '../../validation/validateProcessFlowchartHelpers';
import {
  ensureFlowchartHasOutputNode,
  getValidationErrorForEdge,
} from '../../validation/validateProcessFlowchartRules';
import {healDataFlowEdges} from './healFlowchartUtils';

function ensureTransitionHasDataFlowEdge(
  fromNode: ProcessNodeForValidation,
  toNode: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): boolean {
  return flowchart.edges.some(
    (e) => e.type === ProcessFlowchartEdgeType.DATA_FLOW && e.target === toNode.id && e.source === fromNode.id
  );
}

function ensureControlFlowEdgeIsValid(
  edge: ProcessEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): Array<ProcessFlowchartValidationError> | ProcessFlowchartValidationError | null {
  if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  if (BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_ZERO_FIELD_MAPPERS) {
    return null;
  }

  const fromNode = getFlowchartNodeFromClientId(flowchart, edge.source);
  const toNode = getFlowchartNodeFromClientId(flowchart, edge.target);

  if (fromNode == null || toNode == null) {
    return null;
  }

  if (toNode.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    // From the perspective of workflows-specific validation any node can have an edge to a router
    // No data flow edges are needed when connecting to a router since routers aren't task schemas
    return null;
  }

  if (fromNode?.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    if (toNode.nodeType === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
      return null;
    }

    const taskNodesBeforeRouter = getTaskNodesUpstreamOfNode(fromNode.id, flowchart);
    for (const taskNode of taskNodesBeforeRouter) {
      assert(
        taskNode.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
          taskNode.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
        'Task nodes upstream of a router node must be task or root nodes'
      );
      if (!ensureTransitionHasDataFlowEdge(taskNode, toNode, flowchart)) {
        return [
          getValidationErrorForEdge(
            flowchart,
            edge,
            () => `Draw field mappings between ${taskNode.name} and ${toNode.name}`,
            ProcessFlowchartErrorType.CONTROL_FLOW_NO_DATA_FLOW_EDGE
          ),
        ];
      }
    }
  } else if (!ensureTransitionHasDataFlowEdge(fromNode, toNode, flowchart)) {
    return [
      getValidationErrorForEdge(
        flowchart,
        edge,
        () => `Draw field mappings between ${fromNode.name} and ${toNode.name}`,
        ProcessFlowchartErrorType.CONTROL_FLOW_NO_DATA_FLOW_EDGE
      ),
    ];
  }
  return null;
}

export class ProcessFlowchartValidator extends FlowchartValidator {
  validateFlowchartForType(
    flowchart: ProcessFlowchartForValidation,
    validationContext: ValidationContext
  ): Array<ProcessFlowchartValidationError> {
    const {edges} = flowchart;
    const {designSpace} = validationContext;

    let validationErrors: Array<ProcessFlowchartValidationError> = [];
    validationErrors = pushErrors(
      validationErrors,
      ensureFlowchartHasOutputNode({
        nodes: flowchart.nodes,
        designSpace,
      }).error
    );

    for (const edge of edges) {
      validationErrors = pushErrors(validationErrors, ensureControlFlowEdgeIsValid(edge, flowchart));
    }
    return validationErrors;
  }

  healFlowchart(changes: FlowchartChanges): FlowchartChangeGenerator {
    return healDataFlowEdges(changes);
  }

  readonly errorTypeToMessageOverride: Partial<Record<ProcessFlowchartErrorType, string>> = {};
}
